import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertShuttleBody, Shuttle, UpdateShuttleBody } from "./types/shuttles";

export const useShuttlesApi = () => {
  const api = useApi();

  const useAdminSystemShuttles = useCallback(
    (systemTypeId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/patrol/system_types/${systemTypeId}/shuttles`, query);
      return useSWR<Paginated<Shuttle>, CustomError, string>(url, (url) =>
        api.get<Paginated<Shuttle>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminShuttle = useCallback(
    async (body: InsertShuttleBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/shuttles`;
      const id = await api.post<{ id: string }, InsertShuttleBody>(url, body);
      mutateCache([url, `/admin/settings/patrol/system_types/${body.systemTypeId}/shuttles`]);
      return id;
    },
    [api],
  );

  const updateAdminShuttle = useCallback(
    async (shuttleId: string, body: UpdateShuttleBody): Promise<void> => {
      const url = `/admin/settings/patrol/shuttles/${shuttleId}`;
      await api.put<UpdateShuttleBody>(url, body);
      mutateCache([`/admin/settings/patrol/system_types/${body.systemTypeId}/shuttles`]);
    },
    [api],
  );

  const deleteAdminShuttle = useCallback(
    async (shuttleId: string, systemTypeId: string): Promise<void> => {
      await api.del(`/admin/settings/patrol/shuttles/${shuttleId}`);
      mutateCache([
        `/admin/settings/patrol/system_types/${systemTypeId}/shuttles`,
        `/admin/settings/patrol/system_types/${systemTypeId}/shuttles/${shuttleId}`,
      ]);
    },
    [api],
  );

  const updateAdminShuttleVisibility = useCallback(
    async (
      shuttleId: string,
      visibility: UpdateVisibilityRequestBody,
      options?: { systemTypeId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/patrol/shuttles/${shuttleId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([
        `/admin/settings/patrol/system_types`,
        `/admin/settings/patrol/system_types/${options?.systemTypeId}/shuttles`,
      ]);
    },
    [api],
  );

  return {
    useAdminSystemShuttles,
    insertAdminShuttle,
    updateAdminShuttle,
    updateAdminShuttleVisibility,
    deleteAdminShuttle,
  };
};
